import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  ApiError,
  createUser,
  fetchUsers,
  resetUserPassword,
  searchEmployees,
  setUserActive,
  type UserAccount,
  type UserProfile,
  type UserRole,
} from './api'

function formatDate(value: string | null): string {
  if (!value) return '—'
  return new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
}

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Администратор',
  observer: 'Наблюдатель',
  employee: 'Сотрудник',
}

function CreateUserForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>('admin')
  const [employeeQuery, setEmployeeQuery] = useState('')
  const [employeeId, setEmployeeId] = useState<number | null>(null)

  const employeeOptions = useQuery({
    queryKey: ['user-employee-search', employeeQuery],
    queryFn: () => searchEmployees(employeeQuery),
    enabled: role === 'employee' && employeeQuery.trim().length >= 2,
  })

  const mutation = useMutation({
    mutationFn: () => createUser(email, password, role, role === 'employee' ? employeeId : null),
    onSuccess: () => {
      setEmail('')
      setPassword('')
      setRole('admin')
      setEmployeeQuery('')
      setEmployeeId(null)
      onDone()
    },
  })

  const error = mutation.error instanceof ApiError ? mutation.error.message : null
  const canSubmit = role !== 'employee' || employeeId !== null

  return (
    <form
      className="card"
      onSubmit={(event) => {
        event.preventDefault()
        mutation.mutate()
      }}
    >
      <h2>Новый аккаунт</h2>

      <div className="inline-fields">
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Пароль (от 12 символов)</span>
          <input
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </label>

        <label className="field">
          <span>Роль</span>
          <select
            value={role}
            onChange={(event) => {
              setRole(event.target.value as UserRole)
              setEmployeeId(null)
              setEmployeeQuery('')
            }}
          >
            {(Object.keys(ROLE_LABELS) as UserRole[]).map((value) => (
              <option key={value} value={value}>
                {ROLE_LABELS[value]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {role === 'employee' && (
        <label className="field">
          <span>Карточка сотрудника</span>
          <input
            value={employeeQuery}
            placeholder="Начните вводить имя…"
            onChange={(event) => {
              setEmployeeQuery(event.target.value)
              setEmployeeId(null)
            }}
          />
        </label>
      )}
      {role === 'employee' &&
        employeeOptions.data &&
        employeeQuery.trim().length >= 2 &&
        employeeId === null && (
          <ul className="search-results inline">
            {employeeOptions.data.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setEmployeeId(candidate.id)
                    setEmployeeQuery(candidate.display_name)
                  }}
                >
                  {candidate.display_name}
                </button>
              </li>
            ))}
          </ul>
        )}

      {error && <p className="bad form-error">{error}</p>}

      <button type="submit" disabled={mutation.isPending || !canSubmit}>
        {mutation.isPending ? 'Создаём…' : 'Создать'}
      </button>
    </form>
  )
}

function UserRow({ user, currentUser }: { user: UserAccount; currentUser: UserProfile }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['users'] })
  const handleError = (err: unknown) =>
    setError(err instanceof ApiError ? err.message : 'Не удалось выполнить действие')

  const toggleActive = useMutation({
    mutationFn: () => setUserActive(user.id, !user.is_active),
    onSuccess: () => {
      setError(null)
      refresh()
    },
    onError: handleError,
  })

  const resetPassword = useMutation({
    mutationFn: (newPassword: string) => resetUserPassword(user.id, newPassword),
    onSuccess: () => {
      setError(null)
      // Сброс пароля закрывает все сессии пользователя — список стоит перечитать.
      refresh()
    },
    onError: handleError,
  })

  const isSelf = user.id === currentUser.id

  return (
    <tr className={user.is_active ? undefined : 'muted-row'}>
      <td>
        {user.email}
        {isSelf && <span className="badge">это вы</span>}
      </td>
      <td>{ROLE_LABELS[user.role]}</td>
      <td>{user.is_active ? 'активен' : 'отключён'}</td>
      <td>{formatDate(user.last_login_at)}</td>
      <td className="actions">
        <button
          type="button"
          onClick={() => toggleActive.mutate()}
          disabled={toggleActive.isPending || isSelf}
          title={isSelf ? 'Нельзя отключить собственный аккаунт' : undefined}
        >
          {user.is_active ? 'Отключить' : 'Включить'}
        </button>

        <button
          type="button"
          onClick={() => {
            const newPassword = window.prompt(`Новый пароль для ${user.email} (от 12 символов):`)
            if (newPassword) resetPassword.mutate(newPassword)
          }}
          disabled={resetPassword.isPending}
        >
          Сбросить пароль
        </button>

        {error && <span className="bad row-error">{error}</span>}
      </td>
    </tr>
  )
}

export default function UsersPage({ currentUser }: { currentUser: UserProfile }) {
  const queryClient = useQueryClient()
  const users = useQuery({ queryKey: ['users'], queryFn: fetchUsers })

  return (
    <section>
      <CreateUserForm onDone={() => queryClient.invalidateQueries({ queryKey: ['users'] })} />

      <div className="card">
        <h2>Аккаунты</h2>

        {users.isPending && <p>Загружаем…</p>}
        {users.isError && <p className="bad">Не удалось загрузить список</p>}

        {users.data && (
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Последний вход</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {users.data.map((user) => (
                <UserRow key={user.id} user={user} currentUser={currentUser} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  )
}
